import React from 'react';
import { Combobox } from '../combobox';
import { CountryCode } from '@api/WalletClient';

// Map country codes to readable names
const countryNames: Partial<Record<CountryCode, string>> = {
    [CountryCode.UNDEFINED]: 'Undefined',
    [CountryCode.AT]: 'Austria',
    [CountryCode.BE]: 'Belgium',
    [CountryCode.BG]: 'Bulgaria',
    [CountryCode.HR]: 'Croatia',
    [CountryCode.CY]: 'Cyprus',
    [CountryCode.CZ]: 'Czech Republic',
    [CountryCode.DK]: 'Denmark',
    [CountryCode.EE]: 'Estonia',
    [CountryCode.FI]: 'Finland',
    [CountryCode.FR]: 'France',
    [CountryCode.DE]: 'Germany',
    [CountryCode.GR]: 'Greece',
    [CountryCode.HU]: 'Hungary',
    [CountryCode.IE]: 'Ireland',
    [CountryCode.IT]: 'Italy',
    [CountryCode.LV]: 'Latvia',
    [CountryCode.LT]: 'Lithuania',
    [CountryCode.LU]: 'Luxembourg',
    [CountryCode.MT]: 'Malta',
    [CountryCode.NL]: 'Netherlands',
    [CountryCode.PL]: 'Poland',
    [CountryCode.PT]: 'Portugal',
    [CountryCode.RO]: 'Romania',
    [CountryCode.SK]: 'Slovakia',
    [CountryCode.SI]: 'Slovenia',
    [CountryCode.ES]: 'Spain',
    [CountryCode.SE]: 'Sweden',
    [CountryCode.GB]: 'United Kingdom',
    [CountryCode.US]: 'United States',
    [CountryCode.CA]: 'Canada',
    [CountryCode.AU]: 'Australia',
    [CountryCode.NZ]: 'New Zealand',
    [CountryCode.JP]: 'Japan',
    [CountryCode.CN]: 'China',
    [CountryCode.IN]: 'India',
    [CountryCode.BR]: 'Brazil',
    [CountryCode.MX]: 'Mexico',
    [CountryCode.AR]: 'Argentina',
    [CountryCode.CL]: 'Chile',
    [CountryCode.CO]: 'Colombia',
    [CountryCode.PE]: 'Peru',
    [CountryCode.ZA]: 'South Africa',
    [CountryCode.EG]: 'Egypt',
    [CountryCode.NG]: 'Nigeria',
    [CountryCode.KE]: 'Kenya',
    [CountryCode.RU]: 'Russia',
    [CountryCode.TR]: 'Turkey',
    [CountryCode.SA]: 'Saudi Arabia',
    [CountryCode.AE]: 'United Arab Emirates',
    [CountryCode.IL]: 'Israel',
    [CountryCode.KR]: 'South Korea',
    [CountryCode.TH]: 'Thailand',
    [CountryCode.SG]: 'Singapore',
    [CountryCode.MY]: 'Malaysia',
    [CountryCode.ID]: 'Indonesia',
    [CountryCode.PH]: 'Philippines',
    [CountryCode.VN]: 'Vietnam',
    [CountryCode.NO]: 'Norway',
    [CountryCode.CH]: 'Switzerland',
    [CountryCode.IS]: 'Iceland'
};

export function getCountryLabel(code: CountryCode): string {
    return countryNames[code] || code;
}

interface CountryOption {
    code: CountryCode;
    label: string;
}

interface CountrySelectProps {
    value: CountryCode | 'ALL';
    onChange: (value: CountryCode | 'ALL') => void;
    label?: string;
}

export const CountrySelect: React.FC<CountrySelectProps> = ({
    value,
    onChange,
    label = 'Country'
}) => {
    const allOption: CountryOption = {
        code: 'ALL' as CountryCode,
        label: 'All Countries'
    };

    // Priority countries to show first
    const priorityCountries = [
        CountryCode.PL, // Poland
        CountryCode.DE, // Germany
        CountryCode.DK, // Denmark
        CountryCode.NO, // Norway
        CountryCode.AT, // Austria
        CountryCode.CH // Switzerland
    ];

    const allCountries = Object.values(CountryCode)
        .filter((code) => code !== CountryCode.UNDEFINED)
        .map((code) => ({
            code,
            label: `${getCountryLabel(code)} (${code})`
        }));

    // Separate priority and other countries
    const priorityOptions = priorityCountries
        .map((code) => allCountries.find((c) => c.code === code))
        .filter(Boolean) as CountryOption[];

    const otherOptions = allCountries
        .filter((c) => !priorityCountries.includes(c.code))
        .sort((a, b) => a.label.localeCompare(b.label));

    const countryOptions: CountryOption[] = [
        allOption,
        ...priorityOptions,
        ...otherOptions
    ];

    const selectedOption =
        countryOptions.find((opt) => opt.code === value) || allOption;

    return (
        <Combobox
            label={label}
            searchPlaceholder="Search countries..."
            options={countryOptions}
            getOptionLabel={(option) => option.label}
            getOptionKey={(option) => option.code}
            value={selectedOption}
            onChange={(newValue) =>
                onChange(
                    newValue ? (newValue.code as CountryCode | 'ALL') : 'ALL'
                )
            }
        />
    );
};

export default CountrySelect;
